import { IPublicSession, userSession } from 'state/session';
import { user } from 'utils/auth/user';

/**
 * The empty-live-stream-identifier clobber guard, in one place (AVS-4399).
 *
 * `aveSessionId` / `aveChannelName` going out as `''` while a 3D stream is up tears the stream down
 * for everyone in the session. `syncToServer` has always suppressed that on the outbound path, but
 * the re-send driver reaches `updatePublicSession` directly and rebuilds its body from LIVE state,
 * so an identifier that was dirtied by an earlier write and has since been emptied would go out
 * unguarded. Both callers ask the same question here instead of each keeping their own copy.
 *
 * Only guard while in 3D: `experienceSwitchingSanitation` intentionally clears both AVE ids
 * atomically with a 3D->2D switch, and `streamActive` can stay true across that switch (a live
 * session can keep streaming while showing 2D content), so `streamActive` alone cannot tell a
 * legitimate reset from a stale clobber.
 */
export const LIVE_STREAM_IDENTIFIERS = ['aveSessionId', 'aveChannelName'] as const;

export function emptyLiveStreamIdentifiersToOmit(
  candidateKeys: Iterable<keyof IPublicSession>
): (keyof IPublicSession)[] {
  const session = userSession().get();
  const guarding =
    user.isDealer && session.streamActive !== false && !session.endedAt && session.activeExperience === '3D';
  if (!guarding) {
    return [];
  }
  const keys = new Set(candidateKeys);
  return LIVE_STREAM_IDENTIFIERS.filter((key) => keys.has(key) && session[key] === '');
}
